/**
 * App Initialization
 * 
 * This module handles the initialization of mock data when the app loads.
 * It ensures that stock items are generated first, then locations are updated
 * with accurate capacity and utilisation based on the actual stock.
 * 
 * Data is persisted in localStorage to ensure consistency across tabs.
 */

import { generateMockStockItems } from "./mockStockItems";
import { initializeLocationsWithStockData, getInitializedLocations } from "./initializeLocationStock";
import { 
  hasStoredData, 
  storeStockItems, 
  storeLocations,
  clearStoredData 
} from "./dataStore";

/**
 * Initialize the app data
 * This should be called once when the app loads
 */
export const initializeAppData = () => {
  try {
    // Check if we already have data in localStorage
    if (hasStoredData()) {
      console.log('[App Init] Using stored data from localStorage');
      return;
    }

    console.log('[App Init] Generating new mock data...');

    // Generate stock items first
    const stockItems = generateMockStockItems();
    
    // Store stock items
    storeStockItems(stockItems);

    // Initialize locations with the stock data
    initializeLocationsWithStockData(stockItems);
    
    // Get and store the initialized locations
    const locations = getInitializedLocations();
    storeLocations(locations);

    console.log('[App Init] Initialization complete! Data stored in localStorage.');
  } catch (error) {
    console.error('[App Init] Error during initialization:', error);
  }
};

/**
 * Force re-initialization (useful for development/testing)
 */
export const forceReinitialize = () => {
  console.log('[App Init] Forcing re-initialization...');
  clearStoredData();
  initializeAppData();
  // Reload the page to ensure all components use the new data
  window.location.reload();
};

/**
 * Check if the app has been initialized
 */
export const isAppInitialized = () => {
  return hasStoredData();
};
